import { number } from "prop-types";
import React from "react";

import { isValidNumber } from "../../utils/commonFunctions";

/**
 *  CurrencyMapping
 *
 *  Use this for when the amounts are in numbers
 *
 *  @description This component takes a programFee array and a programFeeTitle string and currentSiteMeta
 *  and returns a span tag with the currency symbol and amount. The currency used is the one
 *  from the currentSiteMeta or the one set in the environment variable GATSBY_PROJECT_CURRENCY
 *  if currentSiteMeta is not set. If the currency is not found in the programFee array, the
 *  component will return a blank span.
 *
 * @param {array} programFee array of objects with currency and amount
 * @param {string} programFeeTitle string that should match the key in the programFee objects
 * @param {object} currentSiteMeta object with site meta data
 *
 * @returns {React.ReactElement} span tag with the currency symbol and amount
 */

const CurrencyMapping = ({ programFee, programFeeTitle, currentSiteMeta }) => {
  const currencyToUse = currentSiteMeta?.currency
    ? currentSiteMeta.currency.toLowerCase()
    : process.env.GATSBY_PROJECT_CURRENCY ?? "gbp";

  const programFeeAmount =
    programFee.find(
      (amount) => amount[`${programFeeTitle}Currency`] === currencyToUse
    ) ?? [];

  const currencyAmount = programFeeAmount[`${programFeeTitle}Amount`] ?? 0;

  const currencyFromAmount =
    programFeeAmount[`${programFeeTitle}FromAmount`] ?? 0;
  const currencyToAmount = programFeeAmount[`${programFeeTitle}ToAmount`] ?? 0;
  const currencyCode =
    currentSiteMeta?.currency ?? process.env.GATSBY_PROJECT_CURRENCY ?? "gbp";

  const currencyFormat = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencyDisplay: "code"
  });

  let currencyFormatAmount = "";

  if (isValidNumber(currencyFromAmount)) {
    currencyFormatAmount = [currencyFromAmount, currencyToAmount]
      .filter(isValidNumber)
      .map(currencyFormat.format)
      .join(" - ");
  } else if (isValidNumber(currencyAmount)) {
    currencyFormatAmount = currencyFormat.format(currencyAmount);
  } else {
    currencyFormatAmount = [currencyCode, currencyAmount].join("");
  }

  const currencyAmountDisplay = currencyFormatAmount
    .replaceAll(currentSiteMeta?.currency, currentSiteMeta?.currency_symbol)
    .replaceAll(/\s|&nbsp;/g, "");

  return <span dangerouslySetInnerHTML={{ __html: currencyAmountDisplay }} />;
};

/**
 * CurrencyMappingAlt
 *
 * Use this for when the amounts are in fullwords and not numbers
 *
 * @description: This component takes a programFee array and a programFeeTitle string and currentSiteMeta
 * and returns a span tag with the currency symbol and amount. The currency used is the one
 * from the currentSiteMeta or the one set in the environment variable GATSBY_PROJECT_CURRENCY
 * if currentSiteMeta is not set. If the currency is not found in the programFee array, the
 * component will return a blank span.
 *
 * @param {array} programFee array of objects with currency and amount
 * @param {string} programFeeTitle string that should match the key in the programFee objects
 * @param {object} currentSiteMeta object with site meta data
 *
 * @returns {React.ReactElement} span tag with the currency symbol and amount
 */
const CurrencyMappingAlt = ({
  programFee,
  programFeeTitle,
  currentSiteMeta
}) => {
  const currencyToUse = currentSiteMeta?.currency
    ? currentSiteMeta.currency.toLowerCase()
    : process.env.GATSBY_PROJECT_CURRENCY ?? "gbp";

  const programFeeAmount =
    programFee.find(
      (amount) => amount[`${programFeeTitle}Currency`] === currencyToUse
    ) ?? [];

  const currencyAmount = programFeeAmount[`${programFeeTitle}Amount`] ?? "";

  if (!currencyAmount) return <></>;

  const currencyAmountDisplay = [
    currentSiteMeta?.currency_symbol,
    currencyAmount
  ].join("");

  return <span dangerouslySetInnerHTML={{ __html: currencyAmountDisplay }} />;
};

export { CurrencyMappingAlt };
export default CurrencyMapping;
